import { Tooltip } from 'antd'
import React, { useState, useRef, useEffect } from 'react'
import { LoadingOutlined } from '@ant-design/icons';
import { MdLocationCity, MdClose, MdStar } from 'react-icons/md';
import axiosInstance from '../../Config/api';
import API_ENDPOINTS from '../../Config/api/endpoints';
import { parseApiResponse, extractData, isSuccessResponse } from '../../Config/api/responseModel';
import { mapTourCardToUi } from '../../Config/utils/tourMediaUtils';
import { useFormatMoney } from '../../Config/utils/formatCurrency';

/**
 * Autocomplete for Things to Do — uses Tour destinations/search APIs, not vehicle addresses.
 */
const TourDestinationPicker = ({
    id,
    placeholder,
    inputClasses,
    value,
    onChange,
    onSelectDestination,
}) => {
    const [searchValue, setSearchValue] = useState(value || '');
    const [spin, setSpin] = useState(false);
    const [results, setResults] = useState([]);
    const [allDestinations, setAllDestinations] = useState([]);
    const debounceRef = useRef(null);
    const formatMoney = useFormatMoney();

    useEffect(() => {
        const load = async () => {
            try {
                const response = await axiosInstance.get(API_ENDPOINTS.TOUR.DESTINATIONS);
                const apiResponse = parseApiResponse(response);
                if (!isSuccessResponse(apiResponse)) return;
                const dests = extractData(apiResponse) || [];
                setAllDestinations(
                    dests.map((d) => ({
                        id: d.name ?? d.Name,
                        name: d.name ?? d.Name,
                        tourCount: d.tourCount ?? d.TourCount ?? 0,
                        type: 'destination',
                    }))
                );
            } catch (error) {
                console.error('Failed to load tour destinations:', error);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (value !== undefined) {
            setSearchValue(value);
        }
    }, [value]);

    const fetchSuggestions = async (query) => {
        const q = (query || '').trim();
        if (q.length < 2) {
            setResults([]);
            setSpin(false);
            return;
        }

        try {
            setSpin(true);
            const lower = q.toLowerCase();
            const destinationMatches = allDestinations
                .filter((d) => d.name.toLowerCase().includes(lower))
                .slice(0, 8);

            const response = await axiosInstance.post(API_ENDPOINTS.TOUR.SEARCH, {
                keyword: q,
                pageNumber: 1,
                pageSize: 8,
            });
            const apiResponse = parseApiResponse(response);
            const data = isSuccessResponse(apiResponse) ? extractData(apiResponse) : null;
            const tours = data?.data ?? data?.Data ?? [];

            const tourMatches = (Array.isArray(tours) ? tours : [])
                .map((t) => {
                    const ui = mapTourCardToUi(t);
                    if (!ui) return null;
                    return {
                        ...ui,
                        name: ui.title,
                        shortDescription: ui.desc,
                        type: 'tour',
                    };
                })
                .filter(Boolean);

            const seen = new Set();
            const combined = [];
            for (const item of [...tourMatches, ...destinationMatches]) {
                const key = `${item.type}-${item.id}`;
                if (seen.has(key)) continue;
                seen.add(key);
                combined.push(item);
            }
            setResults(combined.slice(0, 12));
        } catch (error) {
            console.error('Failed to search tour destinations:', error);
            setResults([]);
        } finally {
            setSpin(false);
        }
    };

    const debounceSearch = (query, delay) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(query);
        }, delay);
    };

    const inputChangeValueHandler = (e) => {
        const query = e.target.value;
        setSearchValue(query);
        onChange?.(query);
        onSelectDestination?.(null);
        debounceSearch(query, 400);
    };

    const pickItem = (item) => {
        setSearchValue(item.name);
        onChange?.(item.name);
        onSelectDestination?.(item);
        setResults([]);
    };

    const clearLocation = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        setSearchValue('');
        onChange?.('');
        onSelectDestination?.(null);
        setResults([]);
        setSpin(false);
    };

    const showClear = searchValue.trim().length > 0;
    const inputRightPad =
        showClear && spin ? 'pr-[4.25rem]' : showClear || spin ? 'pr-10' : 'pr-2';

    const renderTourTile = (item) => {
        const rating = Number(item.rating) || 0;
        const reviews = Number(item.reviews) || 0;
        const priceUnitLabel = Number(item.pricingModel) === 2 ? 'per unit' : 'per person';
        const metaParts = [item.durationLabel, item.shortDescription].filter(Boolean);

        return (
            <Tooltip key={`tour-${item.id}`} title={item.shortDescription || item.name} placement="top">
                <button
                    type="button"
                    className="w-full flex items-stretch gap-3 px-3 py-2.5 text-left hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                    onClick={() => pickItem(item)}
                >
                    <div className="h-20 w-[5.5rem] shrink-0 overflow-hidden rounded-md bg-neutral-100 flex items-center justify-center">
                        <img
                            src={item.image}
                            alt=""
                            loading="lazy"
                            className="max-h-full max-w-full object-contain"
                        />
                    </div>
                    <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                        {item.destination ? (
                            <div className="text-[11px] text-neutral-500 truncate">{item.destination}</div>
                        ) : null}
                        <div className="text-sm font-semibold text-[#1a1a1a] line-clamp-2 leading-snug">
                            {item.name}
                        </div>
                        {metaParts.length > 0 ? (
                            <div className="text-xs text-neutral-500 line-clamp-2">
                                {item.durationLabel && item.shortDescription
                                    ? `${item.durationLabel} · ${item.shortDescription}`
                                    : metaParts[0]}
                            </div>
                        ) : null}
                        <div className="mt-auto flex items-end justify-between gap-2 pt-1">
                            <div className="flex items-center gap-0.5 text-xs text-neutral-600 min-w-0">
                                {rating > 0 ? (
                                    <>
                                        <span className="font-medium tabular-nums">{rating.toFixed(1)}</span>
                                        <MdStar className="text-amber-500 shrink-0" size={14} aria-hidden />
                                        {reviews > 0 ? (
                                            <span className="text-neutral-500 truncate">({reviews})</span>
                                        ) : null}
                                    </>
                                ) : (
                                    <span className="text-neutral-400">No reviews</span>
                                )}
                            </div>
                            <div className="flex flex-col items-end shrink-0 text-right">
                                <span className="text-[10px] leading-none text-neutral-500">From</span>
                                <span className="text-sm font-bold text-[#13253F] whitespace-nowrap leading-tight">
                                    {formatMoney(item.price, { style: 'symbol' })}
                                </span>
                                <span className="text-[10px] text-neutral-500 whitespace-nowrap">{priceUnitLabel}</span>
                            </div>
                        </div>
                    </div>
                </button>
            </Tooltip>
        );
    };

    const renderDestinationRow = (item) => (
        <Tooltip key={`destination-${item.id}`} title={item.name} placement="top">
            <button
                type="button"
                className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-neutral-100 border-b border-neutral-100 last:border-b-0"
                onClick={() => pickItem(item)}
            >
                <MdLocationCity className="mt-0.5 shrink-0 text-[#7BBCB0]" size={18} />
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#1a1a1a] break-words whitespace-normal">{item.name}</div>
                    <div className="text-xs text-neutral-500 break-words whitespace-normal">
                        {`${item.tourCount || 0} experiences`}
                    </div>
                </div>
            </button>
        </Tooltip>
    );

    return (
        <div className="relative w-full">
            <input
                id={id}
                type='text'
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                className={`p-2 outline-none w-full truncate ${inputRightPad} ${inputClasses ? inputClasses : ''}`}
                placeholder={placeholder || 'Attraction, activity or destination'}
                value={searchValue}
                onChange={inputChangeValueHandler}
            />
            <div
                className="pointer-events-none absolute right-2 top-1/2 z-[2] flex -translate-y-1/2 items-center gap-0.5"
                aria-hidden={!showClear && !spin}
            >
                {spin ? (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center text-[#13253F]">
                        <LoadingOutlined spin className="text-lg" />
                    </span>
                ) : null}
                {showClear ? (
                    <button
                        type="button"
                        className="pointer-events-auto flex h-6 w-6 shrink-0 items-center justify-center text-[#13253F] hover:opacity-70"
                        aria-label="Clear destination"
                        onClick={clearLocation}
                    >
                        <MdClose size={18} />
                    </button>
                ) : null}
            </div>

            {results.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(420px,60vh)] overflow-y-auto bg-white shadow-lg border border-neutral-200">
                    {results.map((item) =>
                        item.type === 'tour' ? renderTourTile(item) : renderDestinationRow(item)
                    )}
                </div>
            )}
        </div>
    );
};

export default TourDestinationPicker;
